/**
 * Tâche 1257 — Carte « Paiements Stripe » (Eric 2026-06-28).
 *
 * Le risque n'est pas que le webhook tombe : c'est qu'il tombe EN SILENCE.
 * Argent encaissé, accès jamais ouvert, et le client qui écrit trois jours
 * plus tard. La carte montre le dernier test du parcours complet, le refus des
 * faux événements, et surtout les abonnements fantômes — avec le bouton qui
 * les règle en un clic.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, ArrowsClockwise, CheckCircle, XCircle, Ghost } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const VERDICTS = {
  green: { label: "Le parcours de paiement fonctionne", cls: "border-emerald-200 bg-emerald-50 text-emerald-900" },
  yellow: { label: "À vérifier", cls: "border-amber-300 bg-amber-50 text-amber-900" },
  red: { label: "ABONNEMENT NON ACTIVÉ", cls: "border-red-300 bg-red-50 text-red-900" },
  unknown: { label: "Jamais testé", cls: "border-zinc-200 bg-zinc-50 text-zinc-700" },
};

const fmt = (iso) => {
  if (!iso) return "jamais";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

export const StripeSentinelCard = ({ initial, awaiting }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/sentinel/stripe/status`, {
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
    }
  }, []);

  // Tâche 1249 — La page fournit déjà les données par l'appel groupé.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/api/sentinel/stripe/run-now`, {}, {
        withCredentials: true,
      });
      setData(d.status);
      const verdict = d.run?.verdict;
      if (verdict === "green") toast.success("Parcours de paiement vérifié : tout fonctionne");
      else if (verdict === "yellow") toast.warning("Parcours correct, mais des abonnements sont à vérifier");
      else toast.error("Le parcours de paiement est en défaut — voir le détail");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test impossible");
    } finally {
      setBusy(false);
    }
  };

  const activate = async (sessionId) => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(
        `${API}/api/sentinel/stripe/activate`,
        { session_id: sessionId },
        { withCredentials: true },
      );
      setData(d.status);
      toast.success(`Abonnement activé (${d.result?.plan}) pour ${d.result?.org_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation impossible");
    } finally {
      setBusy(false);
    }
  };

  if (hidden || !data) return null;

  const tone = VERDICTS[data.verdict] || VERDICTS.unknown;
  const last = data.last_run || {};
  const ghosts = data.ghosts || {};
  const paidNotActive = ghosts.paid_not_active || [];
  const activeNoPayment = ghosts.active_without_payment || [];

  return (
    <section
      className="rounded-lg border border-zinc-200 bg-white p-5"
      data-testid="stripe-sentinel-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CreditCard size={18} weight="bold" className="text-zinc-700" />
          <h2 className="font-semibold text-sm">Paiements Stripe</h2>
        </div>
        <button
          onClick={runNow}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
          data-testid="stripe-sentinel-run-btn"
        >
          <ArrowsClockwise size={13} className={busy ? "animate-spin" : ""} /> Tester maintenant
        </button>
      </div>

      <div
        className={`mt-3 rounded-md border px-3 py-2 text-xs ${tone.cls}`}
        data-testid="stripe-sentinel-verdict"
      >
        <strong>{tone.label}</strong> — dernier test {fmt(last.ran_at)}
        {last.trigger ? ` (${last.trigger})` : ""}. Vérifié chaque jour à{" "}
        {data.scheduled_local}. Une organisation jetable est créée puis effacée à chaque
        passage : aucune donnée client n'est touchée.
      </div>

      {(last.steps || []).map((step) => (
        <div key={step.name} className="mt-3" data-testid={`stripe-step-${step.name}`}>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-800">
            {step.ok ? (
              <CheckCircle size={14} weight="fill" className="text-emerald-600" />
            ) : (
              <XCircle size={14} weight="fill" className="text-red-600" />
            )}
            {step.label}
          </div>
          {step.error && (
            <p className="mt-1 text-xs text-red-700 font-mono">{step.error}</p>
          )}
          <ul className="mt-1 space-y-0.5">
            {(step.checks || []).map((c) => (
              <li key={c.name} className="flex items-baseline justify-between gap-2 text-xs">
                <span className={c.ok ? "text-zinc-600" : "text-red-700 font-semibold"}>
                  {c.ok ? "✓" : "✗"} {c.name}
                </span>
                <span className="font-mono text-zinc-500">{c.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
          <Ghost size={13} /> Abonnements fantômes
        </div>

        {paidNotActive.length === 0 && activeNoPayment.length === 0 ? (
          <p className="mt-1 text-xs text-emerald-700" data-testid="stripe-no-ghosts">
            Aucun écart : chaque paiement encaissé correspond à un abonnement actif.
          </p>
        ) : null}

        {paidNotActive.length > 0 && (
          <div className="mt-2" data-testid="stripe-ghosts-paid">
            <p className="text-xs text-red-800 font-semibold">
              {ghosts.paid_not_active_count} paiement(s) encaissé(s) sans abonnement actif —
              le client a payé et n'a pas son accès.
            </p>
            <ul className="mt-1 space-y-1">
              {paidNotActive.map((g) => (
                <li
                  key={g.session_id}
                  className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs"
                >
                  <span className="text-red-900">
                    <strong>{g.org_name || g.org_id}</strong> · forfait {g.plan || "?"} ·
                    statut {g.org_status} · payé le {fmt(g.updated_at || g.created_at)}
                  </span>
                  <button
                    onClick={() => activate(g.session_id)}
                    disabled={busy}
                    className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    data-testid={`stripe-ghost-activate-${g.session_id}`}
                  >
                    Activer maintenant
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {activeNoPayment.length > 0 && (
          <div className="mt-2" data-testid="stripe-ghosts-active">
            <p className="text-xs text-amber-800 font-semibold">
              {ghosts.active_without_payment_count} organisation(s) active(s) sans aucun
              paiement encaissé — à confirmer (activation manuelle ou accès offert).
            </p>
            <ul className="mt-1 space-y-1">
              {activeNoPayment.map((o) => (
                <li
                  key={o.id}
                  className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"
                >
                  <strong>{o.name || o.id}</strong> · forfait {o.plan || "?"} · activée le{" "}
                  {fmt(o.activated_at)} · échéance {fmt(o.subscription_expires)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!data.stripe_key_configured && (
        <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
           data-testid="stripe-key-missing">
          Aucune clé Stripe n'est configurée sur ce serveur : le parcours de paiement ne
          peut pas fonctionner ici.
        </p>
      )}
    </section>
  );
};

export default StripeSentinelCard;
